import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Form, Input, Select } from "antd";
import moment from "moment";
import message from "@/utils/globalMessage";
import { api } from "@/redux/api";
import { CONTRACT_PRIORITIES } from "./constants";
import { FormColumns } from "./ContractRequestCreate.styles";

export default function ContractRequestForm({
  form,
  facilityIds = [],
  facilities = [],
  initialValues,
  onFinish,
}) {
  const [contractors, setContractors] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const selectedFacility = Form.useWatch("facility", form);
  const selectedServiceCategory = Form.useWatch("service_category", form);
  const filteredContractors = useMemo(() => {
    if (!selectedServiceCategory) return contractors;
    return contractors.filter((contractor) => {
      const categoryIds = contractor.service_category_ids ||
        (contractor.service_categories || []).map((category) => category.id);
      return categoryIds.some((id) => String(id) === String(selectedServiceCategory));
    });
  }, [contractors, selectedServiceCategory]);
  const facilityOptions = useMemo(
    () => (facilities.length ? facilities : facilityIds.map((id) => ({ id, name: `Facility #${id}` }))),
    [facilities, facilityIds]
  );

  useEffect(() => {
    let active = true;
    api.getServiceCategories()
      .then((response) => {
        if (!active) return;
        const payload = response?.data;
        setServiceCategories(Array.isArray(payload) ? payload : payload?.results || []);
      })
      .catch(() => {
        if (active) setServiceCategories([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const fieldValues = Object.fromEntries(
      Object.entries(initialValues || {}).filter(([key]) => key !== "sourceLabel")
    );
    const values = {
      ...fieldValues,
      priority: "Low",
      facility: initialValues?.facility || (facilityIds.length === 1 ? facilityIds[0] : undefined),
      due_date: initialValues?.due_date ? moment(initialValues.due_date) : undefined,
      bid_due_date: initialValues?.bid_due_date ? moment(initialValues.bid_due_date) : undefined,
    };
    if (fieldValues.priority) values.priority = fieldValues.priority;
    form.setFieldsValue(values);
  }, [facilityIds, form, initialValues]);

  useEffect(() => {
    if (!selectedFacility) {
      setContractors([]);
      setEquipment([]);
      return undefined;
    }

    let active = true;
    setLoadingContractors(true);
    api.getContractors(selectedFacility)
      .then((response) => {
        if (active) setContractors(response.data || []);
      })
      .catch(() => {
        if (active) message.error("Failed to load contractors");
      })
      .finally(() => {
        if (active) setLoadingContractors(false);
      });
    api.filterEquipment(`facility=${selectedFacility}&page=1&page_size=500`)
      .then((response) => {
        if (!active) return;
        const payload = response.data || {};
        setEquipment(Array.isArray(payload) ? payload : payload.results || []);
      })
      .catch(() => {
        if (active) setEquipment([]);
      });

    return () => {
      active = false;
    };
  }, [selectedFacility]);

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off" preserve>
      <Form.Item name="workorder" hidden><Input /></Form.Item>
      <Form.Item name="service_request" hidden><Input /></Form.Item>
      <Form.Item name="estimate_budget" hidden><Input /></Form.Item>
      <FormColumns>
        <div className="contract-request-column">
          <Form.Item name="facility" label="Facility" rules={[{ required: true, message: "Please select a facility" }]}>
            <Select
              disabled={Boolean(initialValues?.facility) || facilityOptions.length === 1}
              showSearch
              optionFilterProp="label"
              onChange={() => form.setFieldsValue({ contractor_ids: [] })}
              options={facilityOptions.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true, whitespace: true, message: "Please enter a title" }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="equipment" label="Equipment">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              options={equipment.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item name="service_category" label="Service Category">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              onChange={() => form.setFieldsValue({ contractor_ids: [] })}
              options={serviceCategories.map((item) => ({ value: item.id, label: item.name }))}
            />
          </Form.Item>
          <Form.Item name="priority" label="Priority">
            <Select options={CONTRACT_PRIORITIES.map((value) => ({ value, label: value }))} />
          </Form.Item>
        </div>
        <div className="contract-request-column">
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={7} />
          </Form.Item>
          <Form.Item name="due_date" label="Work Due Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="bid_due_date" label="Bid Due Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="contractor_ids"
            label="Contractors"
            rules={[{ required: true, type: "array", min: 1, message: "Select at least one contractor" }]}
          >
            <Select
              mode="multiple"
              loading={loadingContractors}
              optionFilterProp="label"
              maxTagCount="responsive"
              onChange={(values) => {
                if (values.includes("__all__")) {
                  form.setFieldsValue({ contractor_ids: filteredContractors.map((item) => item.id) });
                }
              }}
              options={[
                { value: "__all__", label: "Select all contractors" },
                ...filteredContractors.map((item) => ({
                  value: item.id,
                  label: item.email ? `${item.name} (${item.email})` : item.name,
                })),
              ]}
            />
          </Form.Item>
        </div>
      </FormColumns>
    </Form>
  );
}
